import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api/apiClient";
import {
  SHOP_FREE_ROLES,
  type AppLanguage,
  type Location,
  type Role,
  type VehicleType,
} from "../api/types";
import { APP_LANGUAGE_OPTIONS } from "../i18n/languages";
import { Chip } from "../components/ui/Chip";
import { SegmentedControl } from "../components/ui/SegmentedControl";
import { parseTypedAmount } from "../utils/typedAmount";
import { AmountReadBack } from "../components/finances/AmountReadBack";

const ROLES: Role[] = ["MANAGER", "FLORIST", "COURIER", "HOUSEKEEPING", "ADMIN", "OWNER"];
const ROLE_LABELS: Record<Role, string> = {
  OWNER: "Owner",
  ADMIN: "Admin",
  MANAGER: "Manager",
  FLORIST: "Florist",
  COURIER: "Courier",
  HOUSEKEEPING: "Housekeeping",
};
/** The job title the server would default to; mirrors `defaultJobTitle`. */
const DEFAULT_JOB_TITLE: Partial<Record<Role, string>> = {
  MANAGER: "Manager",
  FLORIST: "Florist",
  COURIER: "Courier",
  HOUSEKEEPING: "Housekeeping",
};

const VEHICLE_TYPES: VehicleType[] = ["BIKE", "MOTORBIKE", "CAR", "VAN"];
const VEHICLE_LABELS: Record<VehicleType, string> = {
  BIKE: "Bike",
  MOTORBIKE: "Motorbike",
  CAR: "Car",
  VAN: "Van",
};

type PayType = "HOURLY" | "SALARY";
const PAY_TYPE_OPTIONS: Array<{ value: PayType; label: string }> = [
  { value: "HOURLY", label: "Hourly" },
  { value: "SALARY", label: "Monthly salary" },
];

/** Mirrors the server's work-contract limits (`users.service.ts` CONTRACT_*). */
const CONTRACT_MAX_BYTES = 10 * 1024 * 1024;

/** A typed euro amount or percent, or undefined when the box is empty. */
function readOptionalAmount(raw: string, label: string): number | undefined {
  if (raw.trim() === "") return undefined;
  const read = parseTypedAmount(raw);
  if (!read.ok) throw new Error(`${label}: ${read.message}`);
  if (read.value < 0) throw new Error(`${label} must not be negative`);
  return read.value;
}

const EMPTY_FORM = {
  email: "",
  firstName: "",
  lastName: "",
  phoneNumber: "",
  language: "" as AppLanguage | "",
  jobTitle: "Florist",
  role: "FLORIST" as Role,
  assignedLocationId: "",
  // G-103 S2 (OD-84/OD-82): pay day defaults to the 1st.
  payDayOfMonth: "1",
  payType: "HOURLY" as PayType,
  hourlyRateEur: "",
  monthlySalaryEur: "",
  salesCommissionPercent: "",
  vehicleType: "BIKE" as VehicleType,
  vehiclePlate: "",
  courierNotes: "",
  maxConcurrentJobs: "",
};

/**
 * G-145 — one form creates a person whole: who they are (and the language
 * their invitation is written in), their role and shop, their pay, and
 * optionally their contract. Owner only (route-gated and `POST /users`).
 */
export function UserCreatePage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [contract, setContract] = useState<File | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Created and invited, but the contract upload failed — where to finish it. */
  const [contractMissedFor, setContractMissedFor] = useState<{ id: string; name: string } | null>(
    null,
  );

  useEffect(() => {
    void (async () => {
      try {
        const list = await api.listLocations();
        const active = list.filter((l) => l.isActive);
        setLocations(active);
        setForm((f) =>
          f.assignedLocationId ? f : { ...f, assignedLocationId: active[0]?.id ?? "" },
        );
      } catch {
        setLocations([]);
      }
    })();
  }, []);

  const set = <K extends keyof typeof EMPTY_FORM>(key: K, value: (typeof EMPTY_FORM)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const requiresAssignment = form.role === "MANAGER" || form.role === "FLORIST";
  const isCourier = form.role === "COURIER";
  // G-126 (OD-151): owners are paid from profit, not wages — no pay at all.
  const isOwnerAccount = form.role === "OWNER";
  // Couriers are paid per delivery (their payout rule is on the Couriers
  // page): pay day only. Housekeeping is hourly only (OD-82).
  const takesRate = !isOwnerAccount && !isCourier;
  const takesCommission = takesRate && form.role !== "HOUSEKEEPING";
  const mayBeSalaried = takesRate && form.role !== "HOUSEKEEPING";
  const payType: PayType = mayBeSalaried ? form.payType : "HOURLY";

  const pickContract = (file: File | null) => {
    setError(null);
    if (file && file.type !== "application/pdf") {
      setError("The contract must be a PDF.");
      return;
    }
    if (file && file.size > CONTRACT_MAX_BYTES) {
      setError("The contract PDF must be 10 MB or smaller.");
      return;
    }
    setContract(file);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setContractMissedFor(null);
    try {
      const payload: Parameters<typeof api.createUser>[0] = {
        email: form.email.trim().toLowerCase(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phoneNumber: form.phoneNumber.trim() || undefined,
        jobTitle: form.jobTitle.trim() || undefined,
        fullName: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
        role: form.role,
        language: form.language || undefined,
      };
      if (requiresAssignment) {
        if (!form.assignedLocationId) throw new Error("Pick the shop they will work at");
        payload.assignedLocationId = form.assignedLocationId;
      }
      if (!isOwnerAccount) {
        const payDay = Number(form.payDayOfMonth);
        if (!Number.isInteger(payDay) || payDay < 1 || payDay > 31) {
          throw new Error("Pay day must be a day of the month, 1–31");
        }
        if (payDay !== 1) payload.payDayOfMonth = payDay;
      }
      if (takesRate) {
        if (payType === "SALARY") {
          // Choosing "Monthly salary" and leaving it empty would quietly make them hourly.
          payload.monthlySalaryEur = readOptionalAmount(form.monthlySalaryEur, "Monthly salary");
          if (payload.monthlySalaryEur === undefined) {
            throw new Error("Type the monthly salary, or choose Hourly");
          }
        } else {
          payload.hourlyRateEur = readOptionalAmount(form.hourlyRateEur, "Hourly rate");
        }
        if (takesCommission) {
          const commission = readOptionalAmount(form.salesCommissionPercent, "Commission");
          if (commission !== undefined && commission > 100) {
            throw new Error("Commission is a percent, 0–100");
          }
          payload.salesCommissionPercent = commission;
        }
      }
      if (isCourier) {
        payload.vehicleType = form.vehicleType;
        payload.vehiclePlate = form.vehiclePlate.trim() || undefined;
        payload.courierNotes = form.courierNotes.trim() || undefined;
        payload.maxConcurrentJobs = form.maxConcurrentJobs
          ? Number(form.maxConcurrentJobs)
          : undefined;
      }

      const created = await api.createUser(payload);
      if (contract) {
        try {
          await api.uploadUserWorkContract(created.id, contract);
        } catch {
          // The person exists and is invited; only the PDF is missing.
          setContractMissedFor({ id: created.id, name: payload.fullName ?? payload.email });
          setForm({ ...EMPTY_FORM, assignedLocationId: locations[0]?.id ?? "" });
          setContract(null);
          return;
        }
      }
      navigate(`/app/users?open=${encodeURIComponent(created.id)}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not create user");
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit =
    form.email.length > 4 &&
    form.firstName.trim().length > 0 &&
    form.lastName.trim().length > 0 &&
    (!requiresAssignment || !!form.assignedLocationId);

  const shopNote = SHOP_FREE_ROLES.has(form.role) ? "All shops" : "Not tied to one shop";

  return (
    <section className="user-create">
      <div>
        <Link to="/app/users" className="user-create-back">
          ← Team
        </Link>
        <h2 className="page-title">Create user</h2>
      </div>

      <form className="user-create-form" onSubmit={handleSubmit} noValidate>
        {error ? <div className="alert error">{error}</div> : null}
        {contractMissedFor ? (
          <div className="alert warning">
            {contractMissedFor.name} is created and invited, but the contract did not upload.{" "}
            <Link to={`/app/users?open=${encodeURIComponent(contractMissedFor.id)}`}>
              Add it from their tile
            </Link>
          </div>
        ) : null}

        <section className="card user-create-section" aria-labelledby="uc-person">
          <h3 id="uc-person">Person</h3>
          <div className="form-grid">
            <div className="field full">
              <label htmlFor="email">Email *</label>
              <input
                id="email"
                type="email"
                required
                maxLength={254}
                autoComplete="off"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="firstName">Name *</label>
              <input
                id="firstName"
                type="text"
                required
                maxLength={80}
                value={form.firstName}
                onChange={(e) => set("firstName", e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="lastName">Surname *</label>
              <input
                id="lastName"
                type="text"
                required
                maxLength={80}
                value={form.lastName}
                onChange={(e) => set("lastName", e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="phoneNumber">Phone number</label>
              <input
                id="phoneNumber"
                type="tel"
                maxLength={40}
                placeholder="Optional"
                value={form.phoneNumber}
                onChange={(e) => set("phoneNumber", e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="language">Language</label>
              <select
                id="language"
                value={form.language}
                onChange={(e) => set("language", e.target.value as AppLanguage | "")}
              >
                <option value="">Default — English</option>
                {APP_LANGUAGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value} lang={option.lang}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <section className="card user-create-section" aria-labelledby="uc-role">
          <h3 id="uc-role">Role and shop</h3>
          <div className="field">
            <span className="field-caption" id="uc-role-label">
              Role *
            </span>
            <div className="user-create-roles" role="group" aria-labelledby="uc-role-label">
              {ROLES.map((role) => (
                <Chip
                  key={role}
                  label={ROLE_LABELS[role]}
                  selected={form.role === role}
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      role,
                      jobTitle: DEFAULT_JOB_TITLE[role] ?? f.jobTitle,
                    }))
                  }
                />
              ))}
            </div>
          </div>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="jobTitle">Job title</label>
              <input
                id="jobTitle"
                type="text"
                maxLength={80}
                value={form.jobTitle}
                onChange={(e) => set("jobTitle", e.target.value)}
              />
            </div>
            {requiresAssignment ? (
              <div className="field">
                <label htmlFor="assignedLocationId">Shop *</label>
                <select
                  id="assignedLocationId"
                  required
                  value={form.assignedLocationId}
                  onChange={(e) => set("assignedLocationId", e.target.value)}
                >
                  <option value="">— Select a shop —</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="field">
                <span className="field-caption">Shop</span>
                <div className="user-create-shop-note">{shopNote}</div>
              </div>
            )}
            {isCourier ? (
              <>
                <div className="field">
                  <label htmlFor="vehicleType">Vehicle *</label>
                  <select
                    id="vehicleType"
                    value={form.vehicleType}
                    onChange={(e) => set("vehicleType", e.target.value as VehicleType)}
                  >
                    {VEHICLE_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {VEHICLE_LABELS[type]}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="vehiclePlate">Vehicle plate</label>
                  <input
                    id="vehiclePlate"
                    type="text"
                    maxLength={20}
                    placeholder="Empty for bikes"
                    value={form.vehiclePlate}
                    onChange={(e) => set("vehiclePlate", e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="maxConcurrentJobs">Max deliveries at once</label>
                  <input
                    id="maxConcurrentJobs"
                    type="number"
                    min={1}
                    max={50}
                    step={1}
                    placeholder="No limit"
                    value={form.maxConcurrentJobs}
                    onChange={(e) => set("maxConcurrentJobs", e.target.value)}
                  />
                </div>
                <div className="field">
                  <label htmlFor="courierNotes">Courier notes</label>
                  <input
                    id="courierNotes"
                    type="text"
                    maxLength={500}
                    value={form.courierNotes}
                    onChange={(e) => set("courierNotes", e.target.value)}
                  />
                </div>
              </>
            ) : null}
          </div>
        </section>

        {isOwnerAccount ? null : (
          <section className="card user-create-section" aria-labelledby="uc-pay">
            <h3 id="uc-pay">Pay</h3>
            {mayBeSalaried ? (
              <SegmentedControl
                options={PAY_TYPE_OPTIONS}
                value={payType}
                onChange={(next) => set("payType", next)}
                ariaLabel="Pay type"
              />
            ) : null}
            <div className="form-grid">
              {takesRate && payType === "HOURLY" ? (
                <div className="field">
                  <label htmlFor="hourlyRateEur">Hourly rate (€)</label>
                  <input
                    id="hourlyRateEur"
                    type="text"
                    inputMode="decimal"
                    placeholder="e.g. 14.50"
                    value={form.hourlyRateEur}
                    onChange={(e) => set("hourlyRateEur", e.target.value)}
                  />
                  <AmountReadBack raw={form.hourlyRateEur} />
                </div>
              ) : null}
              {takesRate && payType === "SALARY" ? (
                <div className="field">
                  <label htmlFor="monthlySalaryEur">Monthly salary (€)</label>
                  <input
                    id="monthlySalaryEur"
                    type="text"
                    inputMode="decimal"
                    placeholder="e.g. 1500"
                    value={form.monthlySalaryEur}
                    onChange={(e) => set("monthlySalaryEur", e.target.value)}
                  />
                  <AmountReadBack raw={form.monthlySalaryEur} />
                </div>
              ) : null}
              {takesCommission ? (
                <div className="field">
                  <label htmlFor="salesCommissionPercent">Commission (% of sales)</label>
                  <input
                    id="salesCommissionPercent"
                    type="text"
                    inputMode="decimal"
                    placeholder="Optional"
                    value={form.salesCommissionPercent}
                    onChange={(e) => set("salesCommissionPercent", e.target.value)}
                  />
                </div>
              ) : null}
              <div className="field">
                <label htmlFor="payDayOfMonth">Pay day (day of month) *</label>
                <input
                  id="payDayOfMonth"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={31}
                  required
                  value={form.payDayOfMonth}
                  onChange={(e) => set("payDayOfMonth", e.target.value)}
                />
              </div>
            </div>
          </section>
        )}

        <section className="card user-create-section" aria-labelledby="uc-contract">
          <h3 id="uc-contract">Contract</h3>
          <label className="user-create-drop" htmlFor="contractPdf">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 16V4M7 9l5-5 5 5" />
              <path d="M5 20h14" />
            </svg>
            {contract ? (
              <span>{contract.name}</span>
            ) : (
              <span>
                Add PDF <span className="muted">· optional · up to 10 MB</span>
              </span>
            )}
          </label>
          <input
            id="contractPdf"
            className="visually-hidden"
            type="file"
            accept="application/pdf"
            onChange={(e) => pickContract(e.target.files?.[0] ?? null)}
          />
          {contract ? (
            <button type="button" className="button plain" onClick={() => setContract(null)}>
              Remove PDF
            </button>
          ) : null}
        </section>

        <div className="button-row">
          <button className="button" type="submit" disabled={submitting || !canSubmit}>
            {submitting ? "Sending…" : "Send invitation"}
          </button>
          <Link to="/app/users" className="button plain">
            Cancel
          </Link>
        </div>
      </form>
    </section>
  );
}
